/**
 * What a prior art row says about whether the worker could read the document.
 *
 * The readability check is the worker's read, so the row projects the file's
 * processing status rather than running anything of its own. One table over
 * every status the wire can carry, so a status added later cannot fall through.
 */

import type {
  FileProcessingStatus,
  FileResponse,
} from "@patent-studio/api-types";

export type Readability =
  "reading" | "readable" | "not-readable" | "check-failed";

const BY_STATUS: Record<FileProcessingStatus, Readability> = {
  uploaded: "reading",
  textract_pending: "reading",
  textract_processing: "reading",
  // The text landed; whatever the index does with it, the document was read.
  textract_complete: "readable",
  indexing: "readable",
  indexed: "readable",
  textract_failed: "not-readable",
  // The text was read and the index write failed: the check itself could not
  // finish, which is a different thing from a document nothing could read.
  indexing_failed: "check-failed",
  // A prior art file never takes the render or transcribe arcs, and is never
  // held. Mapped anyway,
  // so the table stays closed over the enum rather than throwing on a status
  // the row was never meant to see.
  render_pending: "reading",
  render_processing: "reading",
  render_complete: "readable",
  render_failed: "not-readable",
  transcribe_pending: "reading",
  transcribe_processing: "reading",
  transcribe_complete: "readable",
  transcribe_failed: "not-readable",
  held: "not-readable",
};

export function readability(status: FileProcessingStatus): Readability {
  return BY_STATUS[status];
}

export const READABILITY_LABELS: Record<Readability, string> = {
  reading: "Reading…",
  readable: "Readable",
  "not-readable": "Not readable",
  "check-failed": "Check could not run",
};

/** The documents the worker could not read: kept in the library, out of the analysis. */
export function excludedAsUnreadable(
  files: readonly FileResponse[],
): FileResponse[] {
  return files.filter(
    (f) => readability(f.processing_status) === "not-readable",
  );
}
